import {createRequestRefreshScheduler} from './requestRefreshScheduler';

export const ALL_REFRESH_SCOPES = [
    'activities',
    'aggregations',
    'asset_groups',
    'assets',
    'collection_shares',
    'collections',
    'lightboxes',
    'storage_folders',
    'taxonomy',
    'upload_counts',
    'upload_status',
];

const assetGroupScopes = klass => ({
    Collection: ['asset_groups', 'collections', 'collection_shares'],
    Lightbox: ['asset_groups', 'lightboxes'],
    StorageFolder: ['asset_groups', 'storage_folders', 'upload_counts', 'upload_status'],
    Tag: ['taxonomy'],
}[klass] || ['asset_groups']);

export const refreshScopesForEvent = data => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.type === 'dataInvalidated') return data.scopes || [];

    switch (data.type) {
        case 'processingProgress':
            if (!(data.attrs?.finished || data.attrs?.aasm_state === 'processing_error')) return ['upload_status'];

            return [
                ...(data.refresh_activities ? ['activities'] : []),
                'assets',
                'aggregations',
                'storage_folders',
                'upload_counts',
                'upload_status',
            ];
        case 'bulkJobProgress':
            if (!(data.done || data.canceled || data.activity?.terminal || ['failed', 'canceled'].includes(data.aasm_state))) return [];
            if (data.refresh_scopes) return data.refresh_scopes;
            return data.activity?.kind === 'taxonomy_import'
                ? ['assets', 'aggregations', 'taxonomy']
                : ['assets', 'aggregations', 'asset_groups', 'upload_counts', 'upload_status'];
        case 'ingestionAssetAdded':
        case 'assetGroupAssetAdded':
            return ['assets', 'aggregations', 'asset_groups', 'upload_counts', 'upload_status'];
        case 'treeChanged':
            return ['assets', 'aggregations', ...assetGroupScopes(data.klass)];
        case 'assetIndexCompleted':
            return ['activities', 'assets', 'aggregations', ...assetGroupScopes(data.klass)];
        case 'backgroundActivity':
            return [];
        case 'metaImportProgress':
            return data.done ? ['assets', 'aggregations', 'taxonomy', 'upload_counts', 'upload_status'] : [];
        case 'assetSpreadsheetImportProgress':
            return data.done ? ['assets', 'aggregations', 'upload_counts', 'upload_status'] : [];
        case 'tagImportProgress':
        case 'metadataEntityImportProgress':
            return data.done ? ['assets', 'aggregations', 'taxonomy'] : [];
        case 'metaImport':
            return data.activity?.state === 'failed'
                ? ['assets', 'aggregations', 'taxonomy', 'upload_counts', 'upload_status']
                : [];
        case 'assetSpreadsheetImport':
            return data.activity?.state === 'failed'
                ? ['assets', 'aggregations', 'upload_counts', 'upload_status']
                : [];
        case 'tagImport':
        case 'metadataEntityImport':
            return data.activity?.state === 'failed'
                ? ['assets', 'aggregations', 'taxonomy']
                : [];
        default:
            return [];
    }
};

export const eventChangesAssetGroupStructure = data =>
    (Array.isArray(data) && data.includes('asset_groups')) ||
    data?.type === 'treeChanged' ||
        (data?.type === 'dataInvalidated' && data.scopes?.includes('asset_groups'));

export const createDataRefreshCoordinator = ({
    refresh,
    refreshCurrentAssetGroup,
    onStatusChange,
    getStatusContext = () => ({}),
    delay = 250,
    retryDelay = 1000,
    maxRetryDelay = 30000,
}) => {
    const pendingScopes = new Set();
    let currentAssetGroupRefreshPending = false;
    let activeStatusContext = {};
    let retryTimeout;
    let nextRetryDelay = retryDelay;
    let disposed = false;
    let refreshing = false;
    let pendingOrganizationId = getStatusContext?.()?.organizationId;
    const clearRetry = () => {
        clearTimeout(retryTimeout);
        retryTimeout = undefined;
    };
    const scheduler = createRequestRefreshScheduler({
        delay,
        debounce: false,
        request: () => {
            clearRetry();
            refreshing = true;
            const scopes = Array.from(pendingScopes).sort();
            pendingScopes.clear();
            const refreshCurrent = currentAssetGroupRefreshPending;
            currentAssetGroupRefreshPending = false;
            activeStatusContext = getStatusContext?.() || {};
            onStatusChange?.({...activeStatusContext, status: 'updating', scopes});
            const statusContext = activeStatusContext;
            // Wait for both branches to settle before retrying either of them.
            // Promise callbacks also turn synchronous refresh errors into retries.
            return Promise.allSettled([
                Promise.resolve().then(() => refresh(scopes)),
                Promise.resolve().then(() => refreshCurrent ? refreshCurrentAssetGroup?.() : undefined),
            ]).then(results => {
                refreshing = false;
                const failure = results.find(result => result.status === 'rejected');
                if (failure) {
                    if (!disposed && statusContext.organizationId === getStatusContext?.()?.organizationId) {
                        scopes.forEach(scope => pendingScopes.add(scope));
                        currentAssetGroupRefreshPending ||= refreshCurrent;
                        retryTimeout = setTimeout(() => {
                            retryTimeout = undefined;
                            if (statusContext.organizationId === getStatusContext?.()?.organizationId) scheduler.schedule();
                        }, nextRetryDelay);
                        nextRetryDelay = Math.min(nextRetryDelay * 2, maxRetryDelay);
                    } else if (!disposed && (pendingScopes.size || currentAssetGroupRefreshPending)) {
                        scheduler.schedule();
                    }
                    throw failure.reason;
                }
                nextRetryDelay = retryDelay;
                if (!disposed && (pendingScopes.size || currentAssetGroupRefreshPending)) scheduler.schedule();
                return {
                    refreshCurrent,
                    refreshResult: results[0].value,
                    currentAssetGroupResult: results[1].value,
                    statusContext,
                };
            });
        },
        applyResult: ({refreshCurrent, refreshResult, currentAssetGroupResult, statusContext}) => onStatusChange?.({
            ...statusContext,
            status: !refreshCurrent && !currentAssetGroupResult?.canceled && refreshResult?.verified !== false
                ? 'up_to_date'
                : 'idle',
        }),
        onError: error => onStatusChange?.({...activeStatusContext, status: 'failed', error}),
    });

    const invalidate = event => {
        if (disposed) return;
        const organizationId = getStatusContext?.()?.organizationId;
        if (organizationId !== pendingOrganizationId) {
            pendingOrganizationId = organizationId;
            pendingScopes.clear();
            currentAssetGroupRefreshPending = false;
            clearRetry();
            nextRetryDelay = retryDelay;
        }
        refreshScopesForEvent(event).forEach(scope => pendingScopes.add(scope));
        if (eventChangesAssetGroupStructure(event)) currentAssetGroupRefreshPending = true;
        // Fresh invalidations join the pending retry instead of bypassing backoff.
        if (!refreshing && !retryTimeout && (pendingScopes.size || currentAssetGroupRefreshPending)) scheduler.schedule();
    };

    return {
        invalidate,
        reconnect: () => { clearRetry(); nextRetryDelay = retryDelay; invalidate(ALL_REFRESH_SCOPES); },
        dispose: () => { disposed = true; clearRetry(); scheduler.dispose(); },
    };
};
